import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { useModal } from '@/hooks/use-modal';

interface DefaultTrigger {
  label: string;
  icon?: React.ReactNode;
  variant?:
    | 'default'
    | 'outline'
    | 'destructive'
    | 'secondary'
    | 'ghost'
    | 'link'
    | 'outlineDestructive';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

interface ConfirmationModalProps {
  title: string;
  message?: string | React.ReactNode;
  children?: React.ReactNode;
  confirmText: string;
  cancelText?: string;
  confirmVariant?: 'default' | 'destructive' | 'error';
  onConfirm: (closeModal: () => void) => void | Promise<void>;
  isLoading?: boolean;
  disabled?: boolean;
  trigger?: React.ReactNode;
  defaultTrigger?: DefaultTrigger;
  maxWidth?: string;
  open?: boolean;
  hideCloseIcon?: boolean;
  hideCancelButton?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

interface ModalStateParams {
  isExternallyControlled: boolean;
  externalOpen?: boolean;
  externalOnOpen?: () => void;
  externalOnClose?: () => void;
  internalModal: {
    open: boolean;
    onOpen: () => void;
    onClose: () => void;
  };
}

/**
 * Resolves modal state management between external control (parent component)
 * and internal control (self-managed). Enables flexible usage patterns.
 */
const getModalState = ({
  isExternallyControlled,
  externalOpen,
  externalOnOpen,
  externalOnClose,
  internalModal,
}: ModalStateParams) => ({
  open: isExternallyControlled ? externalOpen! : internalModal.open,
  onOpen: isExternallyControlled ? externalOnOpen! : internalModal.onOpen,
  onClose: isExternallyControlled ? externalOnClose! : internalModal.onClose,
});

/**
 * A flexible confirmation modal component with dual state management patterns.
 * Supports both self-managed and externally-controlled modal states for maximum flexibility.
 *
 * **Key Features:**
 * - Dual state management: internal (self-managed) or external (parent-controlled)
 * - Custom trigger support with fallback to default button triggers
 * - Async operation handling with loading states and error management
 * - Flexible content rendering: message text, React nodes, or custom children
 * - Internationalization support with customizable text
 * - Accessible design with proper focus management and keyboard navigation
 * - Prevents accidental closes during loading operations
 * - Customizable styling and button variants
 *
 * **State Management Modes:**
 * 1. **Internal State**: Modal manages its own open/close state
 * 2. **External State**: Parent component controls modal visibility
 *
 * **Common Use Cases:**
 * - Delete confirmations with async API calls
 * - Form submission confirmations
 * - Dangerous action warnings (archive, restore, etc.)
 * - Multi-step confirmation flows
 * - Bulk operation confirmations
 *
 * @example
 * ```tsx
 * // Basic delete confirmation (internal state management)
 * <ConfirmationModal
 *   title="Delete Project"
 *   message="Are you sure you want to delete this project? This action cannot be undone."
 *   confirmText="Delete"
 *   onConfirm={async (closeModal) => {
 *     await deleteProject(projectId);
 *     closeModal();
 *   }}
 * />
 *
 * // With custom trigger button
 * <ConfirmationModal
 *   title="Archive Project"
 *   message="This will move the project to archived status."
 *   confirmText="Archive"
 *   confirmVariant="default"
 *   trigger={
 *     <Button variant="outline">
 *       <ArchiveIcon className="w-4 h-4 mr-2" />
 *       Archive Project
 *     </Button>
 *   }
 *   onConfirm={async (closeModal) => {
 *     await archiveProject(project.id);
 *     closeModal();
 *   }}
 * />
 *
 * // With default trigger configuration
 * <ConfirmationModal
 *   title="Reset Settings"
 *   message="This will restore all settings to their default values."
 *   confirmText="Reset"
 *   defaultTrigger={{
 *     label: "Reset to Defaults",
 *     icon: <RefreshCw className="w-4 h-4" />,
 *     variant: "outline"
 *   }}
 *   onConfirm={async (closeModal) => {
 *     await resetUserSettings();
 *     closeModal();
 *   }}
 * />
 *
 * // External state management (for complex flows)
 * function BulkDeleteComponent() {
 *   const [isModalOpen, setIsModalOpen] = useState(false);
 *   const [selectedItems, setSelectedItems] = useState<string[]>([]);
 *
 *   return (
 *     <>
 *       <Button onClick={() => setIsModalOpen(true)}>
 *         Delete Selected ({selectedItems.length})
 *       </Button>
 *
 *       <ConfirmationModal
 *         title="Delete Multiple Items"
 *         message={`Delete ${selectedItems.length} selected items?`}
 *         confirmText="Delete All"
 *         open={isModalOpen}
 *         onOpen={() => setIsModalOpen(true)}
 *         onClose={() => setIsModalOpen(false)}
 *         onConfirm={async (closeModal) => {
 *           await bulkDeleteItems(selectedItems);
 *           setSelectedItems([]);
 *           closeModal();
 *         }}
 *       />
 *     </>
 *   );
 * }
 *
 * // With complex content and form elements
 * <ConfirmationModal
 *   title="Schedule Maintenance"
 *   confirmText="Schedule"
 *   confirmVariant="default"
 *   onConfirm={async (closeModal) => {
 *     const formData = new FormData(formRef.current);
 *     await scheduleMaintenanceWindow(formData);
 *     closeModal();
 *   }}
 * >
 *   <div className="space-y-4">
 *     <p>Select when to schedule the maintenance window:</p>
 *     <form ref={formRef}>
 *       <div className="grid grid-cols-2 gap-4">
 *         <div>
 *           <label>Start Date</label>
 *           <input type="datetime-local" name="startDate" />
 *         </div>
 *         <div>
 *           <label>Duration (hours)</label>
 *           <input type="number" name="duration" min="1" max="24" />
 *         </div>
 *       </div>
 *     </form>
 *   </div>
 * </ConfirmationModal>
 *
 * // With loading states and error handling
 * function DeleteWithFeedback() {
 *   const [isDeleting, setIsDeleting] = useState(false);
 *
 *   return (
 *     <ConfirmationModal
 *       title="Delete Account"
 *       message="This will permanently delete your account and all associated data."
 *       confirmText="Delete Account"
 *       isLoading={isDeleting}
 *       onConfirm={async (closeModal) => {
 *         setIsDeleting(true);
 *         try {
 *           await deleteUserAccount();
 *           toast.success('Account deleted successfully');
 *           closeModal();
 *           navigate('/login');
 *         } catch (error) {
 *           toast.error('Failed to delete account');
 *           setIsDeleting(false);
 *           // Modal stays open for retry
 *         }
 *       }}
 *     />
 *   );
 * }
 * ```
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  title,
  message,
  children,
  confirmText,
  cancelText,
  confirmVariant = 'destructive',
  onConfirm,
  isLoading = false,
  disabled = false,
  trigger,
  defaultTrigger,
  maxWidth = 'max-w-lg',
  open: externalOpen,
  onOpen: externalOnOpen,
  onClose: externalOnClose,
  hideCloseIcon,
  hideCancelButton = false,
}) => {
  const { t } = useTranslation('common');
  const internalModal = useModal();
  const [internalLoading, setInternalLoading] = useState(false);

  // Determine if parent component manages modal state vs self-managed
  const isExternallyControlled = externalOpen !== undefined;
  const modalState = getModalState({
    isExternallyControlled,
    externalOpen,
    externalOnOpen,
    externalOnClose,
    internalModal,
  });

  // Combine external loading prop with internal async operation loading
  const combinedLoading = isLoading || internalLoading;
  const finalCancelText = cancelText || t('cancel');

  // Manages loading state during async confirmation operations
  const handleConfirm = async () => {
    try {
      setInternalLoading(true);
      // Pass close function to allow confirmation handler to close modal after success
      await onConfirm(modalState.onClose);
    } finally {
      setInternalLoading(false);
    }
  };

  return (
    <>
      {/* Custom trigger element provided by parent */}
      {trigger && <div onClick={modalState.onOpen}>{trigger}</div>}

      {/* Fallback to default button trigger if no custom trigger */}
      {defaultTrigger && !trigger && (
        <Button
          size={defaultTrigger.size || 'sm'}
          variant={defaultTrigger.variant || 'outline'}
          onClick={modalState.onOpen}
        >
          {defaultTrigger.icon}
          {defaultTrigger.label}
        </Button>
      )}

      <Modal
        open={modalState.open}
        onOpen={modalState.onOpen}
        onClose={modalState.onClose}
        preventClose={combinedLoading}
        className={maxWidth}
        hideCloseIcon={hideCloseIcon}
      >
        <Modal.Title className="mb-2">{title}</Modal.Title>

        {message && (
          <Modal.Body className="mb-4 py-0 text-sm text-muted-foreground">{message}</Modal.Body>
        )}

        {children && <Modal.Body className="mb-4 py-0">{children}</Modal.Body>}

        <Modal.Footer>
          <div className="flex justify-end gap-2">
            {!hideCancelButton && (
              <Button variant="outline" onClick={modalState.onClose} disabled={combinedLoading}>
                {finalCancelText}
              </Button>
            )}
            <ButtonLoadable
              variant={confirmVariant}
              onClick={handleConfirm}
              disabled={disabled}
              isLoading={combinedLoading}
            >
              {confirmText}
            </ButtonLoadable>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};
